// app.module.ts
import { BrowserModule } from "@angular/platform-browser";
import { CUSTOM_ELEMENTS_SCHEMA, inject, NgModule, provideAppInitializer } from "@angular/core";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { FlexLayoutModule } from "@angular/flex-layout";
import { SharedModule } from "./shared/shared/shared.module";

// Material Modules
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatRadioModule } from "@angular/material/radio";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatListModule } from "@angular/material/list";
import { MatDialogModule } from "@angular/material/dialog";
import { MatInputModule } from "@angular/material/input";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatTableModule } from "@angular/material/table";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatCardModule } from "@angular/material/card";
import { MatSelectModule } from "@angular/material/select";
import { MatOptionModule } from "@angular/material/core";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatChipsModule } from "@angular/material/chips";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatPaginatorModule } from "@angular/material/paginator";
import { MatSortModule } from "@angular/material/sort";
import { MatMenuModule } from "@angular/material/menu";
// import { NgImageSliderModule } from "ng-image-slider";
import { CheckoutModule } from "paytm-blink-checkout-angular";

// Components
import { DesignerComponent } from "./designer/designer.component";
import { PageNotFoundComponent } from "./shared/page-not-found/page-not-found.component";
import { GenerateShapeComponent } from "./generate-shape/generate-shape.component";
import { LocationDetailsComponent } from "./user-details/location-details/location-details.component";
import { ProfileDetailsComponent } from "./user-details/profile-details/profile-details.component";
import { CanvasComponent } from "./canvascomponent/canvascomponent.component";
import { ShapeSelectorComponent } from "./canvascomponent/shape-selector/shape-selector.component";
import { CompassCubeComponent } from "./canvascomponent/compass-cube/compass-cube.component";
import { ActionButtonContainerComponent } from "./canvascomponent/action-button-container/action-button-container.component";
import { GetCountComponent } from "./canvascomponent/get-count/get-count.component";
import { ColourPickerComponent } from "./canvascomponent/colour-picker/colour-picker.component";
import { DashboardComponent } from "./dashboard/dashboard.component";
import { ProductDetailPageComponent } from "./product-detail-page/product-detail-page.component";
import { HeaderComponent } from "./shared/header/header.component";
import { LoginPageComponent } from "./login-page/login-page.component";
import { TrackOrderComponent } from "./shared/track-order/track-order.component";
import { HomePageComponent } from "./dashboard/home-page/home-page.component";
import { ProductViewlistComponent } from "./shared/product-viewlist/product-viewlist.component";
import { CartPageComponent } from "./dashboard/cart-page/cart-page.component";
import { CheckoutPageComponent } from "./dashboard/checkout-page/checkout-page.component";
import { OrderSuccessComponent } from "./order-success/order-success.component";
import { GenericDialogComponent } from "./shared/generic-dialog/generic-dialog.component";
import { AddressComponent } from "./user-details/address/address.component";
import { ProfileComponent } from "./profile/profile.component";
import { OrderDetailsComponent } from "./order-details/order-details.component";
import { PrivacyPageComponent } from "./shared/footer/privacy-page/privacy-page.component";
import { TermConditionComponent } from "./shared/footer/term-condition/term-condition.component";
import { MatStepperModule } from "@angular/material/stepper";
import { ContactComponent } from "./contact/contact.component";
import { PaymentModule } from "./payment/payment.module";
import { MatBadgeModule } from "@angular/material/badge";

import { GenericInterceptor } from "./services/interceptors/generic.interceptor";
import { DashboardAdminPageComponent } from "./admin/dashboard-admin-page/dashboard-admin-page.component";
import { RatingAndReviewComponent } from "./order-details/rating-and-review/rating-and-review.component";
import { MatProgressBarModule } from "@angular/material/progress-bar";

import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatNativeDateModule } from "@angular/material/core";
import { OrderTimelineComponent } from "./shared/order-timeline/order-timeline.component";

import { NgxMaterialTimepickerModule } from "ngx-material-timepicker";
import { SavedStructuresViewComponent } from "./designer/saved-structures-view/saved-structures-view.component";
import { MyStructuresComponent } from "./my-structures/my-structures.component";
import { MyStructuresListComponent } from "./my-structures/my-structures-list/my-structures-list.component";
import { RightNavbarComponent } from "./dashboard/right-navbar/right-navbar.component";
import { BlockDetailsComponentComponent } from "./block-details-component/block-details-component.component";
import { ProductImageComponent } from "./product-image/product-image-gallery.component";
import { BannerComponent } from "./shared/banner/banner.component";
import { WishlistPageComponent } from "./shared/wishlist-page/wishlist-page.component";
import { UserNotificationComponent } from "./user-notification/user-notification.component";
import { RewardsComponent } from "./rewards/rewards.component";
import { Viewer360Component } from "./viewer360/viewer360.component";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { TourMatMenuModule } from "ngx-ui-tour-md-menu";
import { DesignerTourComponent } from "./designer-tour/designer-tour/designer-tour.component";
import { SearchBarComponent } from "./shared/search-bar/search-bar.component";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { NgOtpInputModule } from "ng-otp-input";
import { AddressDetailsComponent } from "./profile/address-details/address-details.component";
import { OverviewComponent } from "./profile/overview/overview.component";
import { ProfileDetailComponent } from "./profile/profile-details/profile-details.component";
import { UserReviewPageComponent } from "./profile/user-review-page/user-review-page.component";
import { ReviewSuccessComponent } from "./profile/review-success/review-success.component";
import { ItemListComponent } from "./profile/item-list/item-list.component";
import { ReturnReplacePageComponent } from "./profile/return-replace-page/return-replace-page.component";
import { OrderStatusComponent } from "./profile/order-status/order-status.component";
import { OrderInfoComponent } from "./profile/order-info/order-info.component";
import { AddressListComponent } from "./dashboard/checkout-page/address-list/address-list.component";
import { AddressFormComponent } from "./dashboard/checkout-page/address-form/address-form.component";
import { ProductReviewPageComponent } from "./product-detail-page/product-review-page/product-review-page.component";
import { FooterComponent } from "./shared/footer/footer.component";
import { ProductCardComponent } from "./shared/product-card/product-card.component";
import { BadgeComponent } from "./shared/badge/badge.component";
import { BestSellersSectionComponent } from "./shared/best-sellers-section/best-sellers-section.component";
import { CategoriesSectionComponent } from "./shared/categories-section/categories-section.component";
import { CategoryCardComponent } from "./shared/category-card/category-card.component";
import { CustomBlocksCtaComponent } from "./shared/custom-blocks-cta/custom-blocks-cta.component";
import { HeroSectionComponent } from "./shared/hero-section/hero-section.component";
import { MapCardToButtonVariantPipe } from "./shared/pipes/map-card-to-button-variant.pipe";
import { ButtonComponent } from "./shared/button/button.component";
import { ClickOutsideDirective } from "./shared/directives/click-outside.directive";
import { NoOrderItemComponent } from "./shared/no-order-item/no-order-item.component";
import { AiSupportChatComponent } from "./shared/ai-support-chat/ai-support-chat.component";
import { IconService } from "./services/icon-service";
import { PromoBannerComponent } from "./shared/promobanner/promo-banner.component";
import { FeatureProductComponent } from "./shared/feature-product/feature-product.component";
import { HighlightComponent } from "./shared/highlight/highlight.component";
import { CreativityComponent } from "./shared/creativity/creativity.component";
import { HomeClientReviewsComponent } from "./shared/home-client-reviews/home-client-reviews.component";
import { InitialsPipe } from "./shared/pipes/initials-pipe";
import { EllipsisDirective } from "./shared/shared/ellipsis.directive";
import { BackgroundComponent } from "./background/background.component";
import { UserLayoutComponent } from "./layouts/user-layout/user-layout.component";
import { AuthLayoutComponent } from "./layouts/auth-layout/auth-layout.component";
import { AdminLayoutComponent } from "./layouts/admin-layout/admin-layout.component";
import { UserDetailsFormComponent } from "./user-details/user-details-form/user-details-form.component";
import { PlainLayoutComponent } from "./layouts/plain-layout/plain-layout.component";
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ProfileFormComponent } from './profile/profile-details/profile-form/profile-form.component';
import { RelativeTimePipe } from "./shared/shared/relative-time.pipe";
import { AboutUsComponent } from "./shared/footer/about-us/about-us.component";

export function HttpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http, "./assets/i18n/user-module/", ".json");
}

@NgModule({
    declarations: [
        AppComponent,
        DesignerComponent,
        PageNotFoundComponent,
        CanvasComponent,
        GenerateShapeComponent,
        ShapeSelectorComponent,
        CompassCubeComponent,
        GetCountComponent,
        ColourPickerComponent,
        DashboardComponent,
        HeaderComponent,
        FooterComponent,
        LoginPageComponent,
        BackgroundComponent,
        TrackOrderComponent,
        HomePageComponent,
        ProductDetailPageComponent,
        ProductViewlistComponent,
        ActionButtonContainerComponent,
        CartPageComponent,
        CheckoutPageComponent,
        AddressListComponent,
        AddressFormComponent,
        OrderSuccessComponent,
        GenericDialogComponent,
        LocationDetailsComponent,
        ProfileDetailsComponent,
        ReviewSuccessComponent,
        ItemListComponent,
        ReturnReplacePageComponent,
        AddressComponent,
        OrderInfoComponent,
        OrderStatusComponent,
        ProfileComponent,
        AddressDetailsComponent,
        OverviewComponent,
        OrderDetailsComponent,
        UserReviewPageComponent,
        PrivacyPageComponent,
        TermConditionComponent,
        ContactComponent,
        DashboardAdminPageComponent,
        OrderTimelineComponent,
        RatingAndReviewComponent,
        SavedStructuresViewComponent,
        MyStructuresComponent,
        MyStructuresListComponent,
        RightNavbarComponent,
        BlockDetailsComponentComponent,
        ProductImageComponent,
        BannerComponent,
        WishlistPageComponent,
        UserNotificationComponent,
        RewardsComponent,
        Viewer360Component,
        DesignerTourComponent,
        SearchBarComponent,
        ProductReviewPageComponent,
        ProfileDetailComponent,
        ProductCardComponent,
        BadgeComponent,
        BestSellersSectionComponent,
        CategoriesSectionComponent,
        CategoryCardComponent,
        CustomBlocksCtaComponent,
        HeroSectionComponent,
        ButtonComponent,
        NoOrderItemComponent,
        AiSupportChatComponent,
        PromoBannerComponent,
        FeatureProductComponent,
        HighlightComponent,
        CreativityComponent,
        HomeClientReviewsComponent,
        ProfileFormComponent,
        AboutUsComponent,
        UserLayoutComponent,
        AuthLayoutComponent,
        AdminLayoutComponent,
        PlainLayoutComponent,
    ],
    bootstrap: [AppComponent],
    imports: [
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    FlexLayoutModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatFormFieldModule,
    MatIconModule,
    MatButtonModule,
    MatListModule,
    MatDialogModule,
    MatInputModule,
    MatSnackBarModule,
    MatTableModule,
    MatSidenavModule,
    MatCardModule,
    MatSelectModule,
    MatOptionModule,
    MatTooltipModule,
    MatChipsModule,
    PaymentModule,
    MatCheckboxModule,
    MatPaginatorModule,
    MatSortModule,
    MatTableModule,
    MatCheckboxModule,
    MatStepperModule,
    MatMenuModule,
    // NgImageSliderModule,
    CheckoutModule,
    SharedModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressBarModule,
    NgxMaterialTimepickerModule,
    TourMatMenuModule,
    MatBadgeModule,
    MatAutocompleteModule,
    NgOtpInputModule,
    ClickOutsideDirective,
    EllipsisDirective,
    MatSlideToggleModule,
    TranslateModule.forRoot({
        loader: {
            provide: TranslateLoader,
            useFactory: HttpLoaderFactory,
            deps: [HttpClient],
        },
    }),
    MapCardToButtonVariantPipe,
    InitialsPipe,
    UserDetailsFormComponent,
    RelativeTimePipe
],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: GenericInterceptor,
            multi: true,
        },
        provideHttpClient(withInterceptorsFromDi()),
        provideAppInitializer(() => {
            inject(IconService).registerIcons();
        }),
    ],
})
export class AppModule {}
